const LOAD_TIMEOUT_MS = 4000;
const MIN_VISIBLE_HEIGHT = 2;
const RECHECK_DELAY_EARLY_MS = 500;
const RECHECK_DELAY_MID_MS = 1500;
const RECHECK_DELAY_LATE_MS = 2500;
const RECHECK_DELAYS_MS = [RECHECK_DELAY_EARLY_MS, RECHECK_DELAY_MID_MS, RECHECK_DELAY_LATE_MS];
const SCROLL_RESTORE_DELAY_SHORT_MS = 50;
const SCROLL_RESTORE_DELAY_LONG_MS = 150;
const TRUSTPILOT_WRAP_SELECTOR = '[data-trustpilot-reviews-wrap]';
const LIST_SELECTOR = '[data-trustpilot-reviews-list]';
const TRUSTPILOT_WIDGET_SELECTOR = '.trustpilot-widget';
const OBSERVE_SELECTOR = `${TRUSTPILOT_WRAP_SELECTOR}, ${LIST_SELECTOR}`;

let documentObserverStarted = false;
const wrapUpdaters = new WeakMap();

const getAccordionForWrap = (wrap) => wrap?.closest('details') || null;

const isInsideClosedAccordion = (wrap) => {
  const accordion = getAccordionForWrap(wrap);
  return Boolean(accordion && !accordion.open);
};

const isInsideClosedDialog = (wrap) => {
  const dialog = wrap?.closest('dialog');
  return Boolean(dialog && !dialog.open);
};

const isInsideHiddenContainer = (wrap) =>
  isInsideClosedAccordion(wrap) || isInsideClosedDialog(wrap);

const setWrapEmpty = (wrap, isEmpty) => {
  if (!wrap) return;
  wrap.classList.toggle('is-empty', isEmpty);
  wrap.toggleAttribute('hidden', isEmpty);

  if (wrap.matches(LIST_SELECTOR)) {
    const accordion = getAccordionForWrap(wrap);
    if (accordion) {
      const hideAccordion = isEmpty && accordion.open;
      accordion.classList.toggle('hidden', hideAccordion);
      accordion.toggleAttribute('hidden', hideAccordion);
    }
  }
};

const showReviewsAccordionContent = (list) => {
  if (!list) return;

  setWrapEmpty(list, false);

  const accordion = getAccordionForWrap(list);
  accordion?.classList.remove('hidden');
  accordion?.removeAttribute('hidden');
};

const getMainIframe = (widget) => {
  const iframes = [...widget.querySelectorAll('iframe')];
  return (
    iframes.find((iframe) => !/pop-?up|dropdown|modal|layer/i.test(iframe.title || '')) ||
    iframes[0] ||
    null
  );
};

const isWidgetCollapsed = (widget) => {
  if (!widget) return false;

  const widgetStyle = window.getComputedStyle(widget);
  if (widgetStyle.display === 'none' || widgetStyle.visibility === 'hidden') {
    return true;
  }

  const mainIframe = getMainIframe(widget);
  if (!mainIframe) {
    return false;
  }

  const iframeStyle = window.getComputedStyle(mainIframe);
  if (iframeStyle.display === 'none' || iframeStyle.visibility === 'hidden') {
    return true;
  }

  return (
    mainIframe.offsetHeight <= MIN_VISIBLE_HEIGHT && widget.offsetHeight <= MIN_VISIBLE_HEIGHT
  );
};

const getObservationTarget = (element) => {
  if (element.matches(TRUSTPILOT_WIDGET_SELECTOR)) {
    return {
      widget: element,
      wrap: element.closest(TRUSTPILOT_WRAP_SELECTOR) || element.parentElement,
    };
  }

  if (element.matches(TRUSTPILOT_WRAP_SELECTOR)) {
    const widget = element.querySelector(TRUSTPILOT_WIDGET_SELECTOR);
    return widget ? { widget, wrap: element } : null;
  }

  if (element.matches(LIST_SELECTOR)) {
    const widget = element.querySelector(TRUSTPILOT_WIDGET_SELECTOR);
    return widget ? { widget, wrap: element } : null;
  }

  return null;
};

const observeTrustpilot = (element) => {
  const target = getObservationTarget(element);
  if (!target?.wrap || !target.widget) return;
  // Trustpilot throws when bootstrapping inside display:none dialogs (e.g. closed ATC)
  if (isInsideClosedDialog(target.wrap)) return;

  const { widget, wrap } = target;
  if (wrap.dataset.trustpilotObserved === 'true') return;
  wrap.dataset.trustpilotObserved = 'true';

  let hasSeenIframe = false;
  let settled = false;

  const update = () => {
    if (isInsideHiddenContainer(wrap)) {
      return;
    }

    const mainIframe = getMainIframe(widget);
    if (mainIframe) {
      hasSeenIframe = true;
    }

    const collapsed = isWidgetCollapsed(widget);
    const noIframeAfterLoad = settled && !mainIframe && widget.offsetHeight <= MIN_VISIBLE_HEIGHT;

    if ((collapsed && hasSeenIframe) || noIframeAfterLoad) {
      setWrapEmpty(wrap, true);
      return;
    }

    if (!collapsed) {
      setWrapEmpty(wrap, false);
    }
  };

  wrapUpdaters.set(wrap, update);

  const resizeObserver = new ResizeObserver(update);
  resizeObserver.observe(widget);

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeName === 'IFRAME') {
          node.addEventListener('load', update);
        }
      });
    });
    update();
  });

  mutationObserver.observe(widget, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class', 'hidden'],
  });

  widget.querySelectorAll('iframe').forEach((iframe) => {
    iframe.addEventListener('load', update);
  });

  setTimeout(() => {
    settled = true;
    update();
  }, LOAD_TIMEOUT_MS);

  RECHECK_DELAYS_MS.forEach((delay) => {
    setTimeout(update, delay);
  });

  update();
};

const scanTrustpilotWidgets = (root = document) => {
  root.querySelectorAll(OBSERVE_SELECTOR).forEach(observeTrustpilot);
};

const startDocumentObserver = () => {
  if (documentObserverStarted || !document.body) return;
  documentObserverStarted = true;

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        if (node.matches?.(OBSERVE_SELECTOR)) {
          observeTrustpilot(node);
        }

        node.querySelectorAll?.(OBSERVE_SELECTOR).forEach(observeTrustpilot);
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

const reloadTrustpilotWidgets = (root, { force = false } = {}) => {
  if (!root) return;

  root.querySelectorAll(TRUSTPILOT_WIDGET_SELECTOR).forEach((widget) => {
    if (isInsideClosedDialog(widget)) return;

    const mainIframe = getMainIframe(widget);
    const needsReload =
      force || !mainIframe || mainIframe.offsetHeight <= MIN_VISIBLE_HEIGHT;

    if (!needsReload || !window.Trustpilot?.loadFromElement) return;

    widget.querySelectorAll('iframe').forEach((iframe) => {
      iframe.setAttribute('tabindex', '-1');
    });

    window.Trustpilot.loadFromElement(widget, true);
  });
};

const keepScrollPosition = (callback) => {
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  callback();

  const restore = () => {
    window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
  };

  requestAnimationFrame(() => {
    restore();
    requestAnimationFrame(restore);
  });

  setTimeout(restore, SCROLL_RESTORE_DELAY_SHORT_MS);
  setTimeout(restore, SCROLL_RESTORE_DELAY_LONG_MS);
};

let accordionReloadBound = false;

const bindTrustpilotAccordionReload = () => {
  if (accordionReloadBound) return;
  accordionReloadBound = true;

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (details.tagName !== 'DETAILS' || !details.open) return;

      const list = details.querySelector(LIST_SELECTOR);
      if (!list) return;

      keepScrollPosition(() => {
        showReviewsAccordionContent(list);
        reloadTrustpilotWidgets(details);
        observeTrustpilot(list);
        wrapUpdaters.get(list)?.();
      });
    },
    true
  );
};

const initTrustpilotProductReviews = (root = document) => {
  scanTrustpilotWidgets(root);
  startDocumentObserver();
  bindTrustpilotAccordionReload();
};

export default initTrustpilotProductReviews;
